import React from "react";
import { ArrowRight, Flame, Home, Wrench, Zap } from "lucide-react";
import { trackQuote } from "@/lib/track";
import { scrollToId } from "@/lib/scrollToId";
import { Link } from "@/lib/router-compat";

const services = [
  {
    icon: Flame,
    title: "Underfloor Heating Screed",
    description:
      "We pour screed straight over your heating pipes. It wraps around them with no air gaps, so your rooms warm up faster and cost less to heat.",
    link: "/services/underfloor-heating-screed",
    linkText: "underfloor heating screed",
  },
  {
    icon: Wrench,
    title: "Heavy-Duty Screed",
    description:
      "Tough, hard-wearing screed that handles heavy use, ideal for garages, workshops, and busy commercial spaces.",
    link: "/services/heavy-duty-screed",
    linkText: "heavy-duty screed",
  },
  {
    icon: Home,
    title: "Ready-To-Tile Screed",
    description:
      "Perfect for new homes and extensions. We can lay up to 1,000m² in a day, with a laser-flat finish ready for your final flooring.",
    link: "/services/ready-to-tile-screed",
    linkText: "ready-to-tile screed",
  },
  {
    icon: Zap,
    title: "Rapid-Drying Screed",
    description:
      "Got a tight deadline? Our fast-drying screed can be ready for your floor covering in as little as 10 to 14 days. We test the moisture before giving the all-clear.",
    link: "/services/fast-drying-screed",
    linkText: "fast-drying screed",
  },
];

export const WhatWeDo: React.FC = () => {
  return (
    <section
      id="what-we-do"
      className="py-20 bg-[#12121D] text-white relative overflow-hidden"
    >
      {/* Ambient glow */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(212,175,55,0.08)_0%,transparent_50%)] pointer-events-none" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_80%,rgba(212,175,55,0.06)_0%,transparent_50%)] pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-12">
          <span className="text-xs sm:text-sm uppercase tracking-widest text-[#D4AF37] font-bold block mb-2">
            WHAT WE DO
          </span>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-white tracking-tight mb-4">
            Floors for <span className="gold-gradient-text">Every Project</span>
          </h2>
          <p className="text-stone-400 text-base sm:text-lg leading-relaxed">
            From domestic extensions to commercial installations and
            agricultural pours, we supply the ideal liquid screed mix for your
            property.
          </p>
        </div>

        {/* Service Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          {services.map((s, i) => {
            const Icon = s.icon;
            return (
              <div
                key={i}
                className="bg-[#0A0A14] border border-[#D4AF37]/40 rounded-2xl p-6 sm:p-8 shadow-lg hover:shadow-[0_0_30px_rgba(212,175,55,0.15)] hover:border-[#D4AF37]/70 transition-all duration-300 flex flex-col"
              >
                <div className="p-3.5 rounded-xl bg-[#D4AF37]/15 text-[#D4AF37] mb-5 w-fit">
                  <Icon className="w-7 h-7" />
                </div>
                <h3 className="text-xl font-bold text-white mb-3 tracking-tight">
                  {s.title}
                </h3>
                <p className="text-stone-400 text-sm sm:text-base leading-relaxed mb-5 flex-1">
                  {s.description}
                </p>
                <Link
                  to={s.link}
                  className="inline-flex items-center gap-2 text-[#D4AF37] hover:text-[#FFF1C5] font-bold text-sm tracking-wide transition-colors group w-fit"
                >
                  <span>Learn more about {s.linkText}</span>
                  <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                </Link>
              </div>
            );
          })}
        </div>

        {/* Important Floor Preparation - Dark Card */}
        <div className="mt-10">
          <div className="relative rounded-[2rem] overflow-hidden bg-[#0A0A14] border border-[#D4AF37]/30 p-8 md:p-12 shadow-[0_0_50px_rgba(212,175,55,0.15)]">
            {/* Radial glow background */}
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(212,175,55,0.2)_0%,transparent_50%)] pointer-events-none" />

            {/* Left vertical gold strip */}
            <div className="absolute left-0 top-0 bottom-0 w-2 bg-gradient-to-b from-[#FFF1C5] via-[#D4AF37] to-[#8A6715]" />

            <div className="relative z-10 flex flex-col md:flex-row items-center justify-between gap-8">
              <div className="max-w-2xl">
                <span className="text-xs text-[#D4AF37] font-semibold uppercase tracking-[0.2em] mb-3 block">
                  Important Floor Preparation
                </span>
                <h3 className="text-2xl md:text-4xl font-semibold mb-3 text-white tracking-tight leading-snug">
                  Installing Underfloor Heating? <br />
                  <span className="gold-gradient-text">
                    Don't ruin it with traditional screed.
                  </span>
                </h3>
                <p className="text-stone-400 text-sm md:text-base leading-relaxed">
                  Traditional sand and cement leaves air gaps around heating
                  pipes that waste energy. Liquid Cemfloor wraps tightly around
                  every pipe, so heat spreads evenly and your energy bills stay
                  lower for good.
                </p>
              </div>

              <div className="shrink-0 w-full md:w-auto">
                <a
                  href="#contact"
                  onClick={(e) => {
                    e.preventDefault();
                    trackQuote("what_we_do_card");
                    scrollToId("#contact");
                  }}
                  className="inline-flex items-center justify-center gap-2 text-sm font-bold bg-gradient-to-r from-[#FFF1C5] via-[#D4AF37] to-[#A37F1B] text-stone-950 py-3.5 px-8 rounded-full hover:scale-105 transition-all shadow-[0_0_20px_rgba(212,175,55,0.3)] w-full md:w-auto"
                >
                  <span>Get a Quote</span>
                  <ArrowRight className="w-4 h-4 ml-1" />
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* CTA Bar */}
        <div className="mt-10 text-center">
          <p className="text-stone-400 text-sm mb-4">
            Not sure which screed you need? That's fine, we'll help you choose
            on your free site visit.
          </p>
          <a
            href="#contact"
            onClick={(e) => {
              e.preventDefault();
              trackQuote("what_we_do_site_visit");
              scrollToId("#contact");
            }}
            className="inline-flex items-center gap-2 bg-[#C9A84C] hover:bg-[#b89539] text-stone-950 font-bold text-sm sm:text-base px-7 py-3.5 rounded-md shadow-md hover:shadow-lg transition-all group"
          >
            <span>Book Your Free Site Visit</span>
            <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
          </a>
        </div>
      </div>
    </section>
  );
};
